// @core/stores/secret/SecretStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { secretApi } from '@core/api';
import type {
  CreateEncryptionKeyRequest,
  DecryptRequest,
  DecryptResponse,
  EncryptRequest,
  EncryptResponse,
  EncryptionKeyResponse,
  GenerateDataKeyRequest,
  GenerateDataKeyResponse,
  GenerateRandomBytesRequest,
  GenerateRandomBytesResponse,
  ListEncryptionKeysResponse,
  RewrapRequest,
  RotateEncryptionKeyRequest,
  SignRequest,
  SignResponse,
  UpdateEncryptionKeyConfigRequest,
  VerifyRequest,
  VerifyResponse,
} from '@core/types/secret';

class SecretStore {

  // Loading states
  isLoading = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }

  reset(): void {
    this.error = null;
  }

  /**
   * Create encryption key
   */
  async CreateEncryptionKey(engine_id: string, data: CreateEncryptionKeyRequest): Promise<EncryptionKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.CreateEncryptionKey(engine_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Decrypt data
   */
  async Decrypt(engine_id: string, key_name: string, data: DecryptRequest): Promise<DecryptResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.Decrypt(engine_id, key_name, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete encryption key
   */
  async DeleteEncryptionKey(engine_id: string, key_name: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.DeleteEncryptionKey(engine_id, key_name);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Encrypt data
   */
  async Encrypt(engine_id: string, key_name: string, data: EncryptRequest): Promise<EncryptResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.Encrypt(engine_id, key_name, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Generate data key
   */
  async GenerateDataKey(engine_id: string, key_name: string, data: GenerateDataKeyRequest): Promise<GenerateDataKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.GenerateDataKey(engine_id, key_name, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Generate random bytes
   */
  async GenerateRandomBytes(engine_id: string, data: GenerateRandomBytesRequest): Promise<GenerateRandomBytesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.GenerateRandomBytes(engine_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get encryption key
   */
  async GetEncryptionKey(engine_id: string, key_name: string): Promise<EncryptionKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.GetEncryptionKey(engine_id, key_name);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List encryption keys
   */
  async ListEncryptionKeys(engine_id: string, params?: { limit?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; sort?: string | number | boolean; }): Promise<ListEncryptionKeysResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.ListEncryptionKeys(engine_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Rewrap ciphertext
   */
  async Rewrap(engine_id: string, key_name: string, data: RewrapRequest): Promise<EncryptResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.Rewrap(engine_id, key_name, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Rotate encryption key
   */
  async RotateEncryptionKey(engine_id: string, key_name: string, data: RotateEncryptionKeyRequest): Promise<EncryptionKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.RotateEncryptionKey(engine_id, key_name, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Sign data
   */
  async Sign(engine_id: string, key_name: string, data: SignRequest): Promise<SignResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.Sign(engine_id, key_name, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update encryption key config
   */
  async UpdateEncryptionKeyConfig(engine_id: string, key_name: string, data: UpdateEncryptionKeyConfigRequest): Promise<EncryptionKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.UpdateEncryptionKeyConfig(engine_id, key_name, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Verify signature
   */
  async Verify(engine_id: string, key_name: string, data: VerifyRequest): Promise<VerifyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.Verify(engine_id, key_name, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const secretStore = new SecretStore();
